/**
 * A única porta de download de certificado.
 *
 * O QUE ELA SUBSTITUI
 *
 * Quatro telas baixavam certificado, cada uma com sua sequência própria de
 * montar um preview escondido, esperar as fontes, fotografar a tela e colar a
 * imagem num PDF. Quatro cópias da mesma dança, e elas já divergiam: uma delas
 * (`MeusCertificados`) esquecia de passar `elements` adiante, e o participante
 * recebia um documento com o layout de fábrica em vez do que o produtor
 * desenhou. Foi o bug que originou toda esta reescrita.
 *
 * Agora as quatro perguntam PRIMEIRO ao servidor. O que sobra de específico em
 * cada tela é só o recuo, que morre junto com o `html-to-image`.
 *
 * POR QUE AINDA EXISTE O CAMINHO ANTIGO
 *
 * A edge `render-certificate` só desenha certificado que tenha
 * `render_snapshot` — a fotografia gravada na emissão. Dois casos ficam de
 * fora enquanto a migração não termina:
 *
 *   1. os 149 emitidos antes da Fase B, até o backfill rodar;
 *   2. qualquer ambiente onde a edge ainda não foi publicada.
 *
 * Nesses, a tela desenha no navegador como sempre fez. Não é elegância, é
 * ordem de deploy: sem o recuo, publicar o site antes da edge deixaria todo
 * mundo sem baixar nada.
 *
 * QUANDO APAGAR O RECUO
 *
 * Quando a edge estiver publicada E o backfill tiver rodado. O plano reserva
 * duas semanas com os dois caminhos vivos para a comparação ser possível em
 * produção. Depois disso caem `html-to-image`, `jspdf` e
 * `lib/certificate-pdf-client.ts` inteiro.
 */

import { supabase } from "@/integrations/supabase/client";

export interface PedidoDeCertificado {
  /** Um dos dois. O código é o que as telas públicas têm à mão. */
  certificateCode?: string | null;
  certificateId?: string | null;
  feedbackToken?: string | null;
}

/**
 * Erro que NÃO deve cair no recuo.
 *
 * Certificado revogado ou sessão inválida são respostas legítimas do servidor.
 * Desenhar no navegador nesses casos entregaria ao usuário justamente o
 * documento que o servidor acabou de recusar — e o certificado revogado voltar
 * a sair pela porta dos fundos é exatamente o furo que a Fase B fecha.
 */
export class DownloadRecusado extends Error {
  constructor(
    readonly status: number,
    mensagem: string,
  ) {
    super(mensagem);
    this.name = "DownloadRecusado";
  }
}

export class FeedbackRequiredError extends DownloadRecusado {
  constructor(
    mensagem: string,
    readonly surveyUrl?: string | null,
  ) {
    super(403, mensagem);
    this.name = "FeedbackRequiredError";
  }
}

export function salvarPdf(bytes: Blob, nomeArquivo: string): void {
  const url = URL.createObjectURL(bytes);
  const a = document.createElement("a");
  a.href = url;
  a.download = nomeArquivo;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Sem isto o blob fica na memória da aba até ela fechar. Num painel de
  // produtor que baixa dezenas de certificados em sequência, soma.
  URL.revokeObjectURL(url);
}

/**
 * Busca o PDF na edge.
 *
 * Devolve `null` quando o servidor AINDA não consegue desenhar este
 * certificado — sem snapshot (409), versão de pipeline desconhecida (503),
 * função não publicada (404), rede fora. São os casos de recuo.
 *
 * Lança `DownloadRecusado` quando o servidor recusou de propósito (401, 403).
 */
export async function buscarPdfNoServidor(
  pedido: PedidoDeCertificado,
): Promise<Blob | null> {
  if (!pedido.certificateId && !pedido.certificateCode) return null;

  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  // Sem sessão a edge devolveria 401 de qualquer forma. As telas públicas de
  // verificação caem no recuo, que é o comportamento que já tinham.
  if (!token && !pedido.feedbackToken) return null;

  const parametro = pedido.certificateId
    ? `id=${encodeURIComponent(pedido.certificateId)}`
    : `code=${encodeURIComponent(pedido.certificateCode ?? "")}`;

  let resposta: Response;
  try {
    resposta = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/render-certificate?${parametro}`,
      { headers: pedido.feedbackToken
        ? { "x-feedback-token": pedido.feedbackToken }
        : { Authorization: `Bearer ${token}` } },
    );
  } catch {
    return null;
  }

  if (resposta.ok) {
    const blob = await resposta.blob();
    // Uma edge que responde 200 com JSON de erro já aconteceu neste projeto:
    // `generate-certificate-pdf` promete PDF no nome e devolve JSON, e o
    // usuário baixava um .pdf que leitor nenhum abre, com aviso de sucesso.
    // Falha silenciosa é a pior forma; conferir o tipo custa uma linha.
    if (blob.type && !blob.type.includes("pdf")) return null;
    return blob;
  }

  if (resposta.status === 401 || resposta.status === 403) {
    const corpo = await resposta.json().catch(() => ({}) as { error?: string; error_code?: string; survey_url?: string });
    if (corpo?.error_code === "feedback_required") {
      throw new FeedbackRequiredError(
        corpo.error ?? "É necessário responder à pesquisa de satisfação para acessar o certificado.",
        corpo.survey_url,
      );
    }
    throw new DownloadRecusado(
      resposta.status,
      corpo?.error ?? "Este certificado não está disponível para download.",
    );
  }

  return null;
}

/**
 * Tenta o servidor e salva. `true` = pronto; `false` = a tela deve recuar.
 *
 * É o formato que as telas conseguem usar hoje: o desenho no navegador delas é
 * dirigido por efeito, não por `await`, então quem chama precisa saber apenas
 * se deve ou não seguir pelo caminho antigo.
 */
export async function baixarCertificadoDoServidor(
  pedido: PedidoDeCertificado,
  nomeArquivo: string,
): Promise<boolean> {
  const pdf = await buscarPdfNoServidor(pedido);
  if (!pdf) return false;
  salvarPdf(pdf, nomeArquivo);
  return true;
}

/**
 * O PDF de EXEMPLO de um evento, desenhado pelo servidor.
 *
 * É o botão "Ver PDF real" do editor: o mesmo `resolve` e o mesmo `renderPdf`
 * que produzem o arquivo do participante, com um participante fictício. Até
 * existir, a única forma de o produtor ver o documento de verdade era disparar
 * para alguém.
 *
 * Devolve `null` quando o servidor não pode desenhar agora — edge não
 * publicada, rede fora. Quem chama recua para o desenho no navegador.
 */
export async function buscarPdfDeExemplo(eventId: string): Promise<Blob | null> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) return null;

  let resposta: Response;
  try {
    resposta = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/render-certificate?preview=${encodeURIComponent(eventId)}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
  } catch {
    return null;
  }

  if (!resposta.ok) return null;

  const blob = await resposta.blob();
  if (blob.type && !blob.type.includes("pdf")) return null;
  return blob;
}
